import { useLingui } from '@lingui/react/macro';
import { Zap } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { EXPAND_STEP_ROOMY, Expand, ExpandGroup } from '../expand';
import { Karaoke } from '../karaoke';
import { DiamondGrid, Squiggle, diamondGlows } from '../primitives';

/**
 * The dot that closes the vertical rule between the two statements: an 8px olive disc with a 4px hole
 * punched in it, which the hand-off draws as two stacked circles rather than a ring.
 */
function RuleDot(props: { className?: string }) {
	return (
		<span aria-hidden className={cn('bg-olive relative size-2 shrink-0 rounded-full', props.className)}>
			<span className="bg-ink absolute top-1/2 left-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full" />
		</span>
	);
}

/**
 * "Worldcoo nació en Barcelona en 2012…" — the story that opens About.
 *
 * One ink band holding two centred statements with the photo collage between them. The hand-off splits
 * it into two frames of 643 and 919, but they share a background and read as one stretch, so it is a
 * single section here.
 *
 * The photos deliberately BLEED off both edges — the export puts the left one at x=-185 of a 1440 canvas
 * — which is why they are positioned rather than laid out in the grid. Below `xl` they come inside and
 * stack under the card, since a photo cropped to a sliver on a phone reads as a mistake.
 */
export function Story() {
	const { t } = useLingui();
	// `overflow-clip`, not `overflow-hidden`: `hidden` makes a box a SCROLL CONTAINER, and a view
	// timeline resolves against its nearest ancestor scroll container — so under `hidden` every timeline
	// in here would measure itself against this section's own box, which never scrolls, and sit frozen
	// on its first frame. `clip` clips identically and is explicitly not a scroll container.
	return (
		<section className="bg-ink relative overflow-clip text-white">
			{/* The dotted texture, HELD against the viewport. This band is two pinned scenes, so for most of
						    its length the content is deliberately frozen — and a texture painted across the section went
						    on sliding behind it. The absolute wrapper keeps the sticky layer out of the flow.

						    The two glows are Figma's, restated against the screen: a band that is now four screens of
						    held scenes has no meaningful percentage down its own length. */}
			<div aria-hidden className="pointer-events-none absolute inset-0">
				<div className="sticky top-0 h-screen">
					<DiamondGrid
						color="rgba(255,255,255,0.35)"
						opacity={1}
						masks={[diamondGlows({ x: '50%', y: '22%' }, { x: '50%', y: '72%' })]}
					/>
				</div>
			</div>

			<div className="new-story-band relative mx-auto max-w-[1440px] px-6 py-20 md:py-28 xl:px-10">
				{/* The opening statement is HELD while the scroll reads it out. The stage is the scroll the pin
								    spends; see the `new-story-stage` block in `-styles/theme.css`. */}
				<div className="new-story-stage">
					<div className="new-story-pin">
						<Karaoke
							className="mx-auto max-w-[1022px] text-center text-2xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]"
							text={t`Worldcoo was born in Barcelona in 2012 with a very clear purpose: to help NGOs find the funds they need to drive their projects forward.`}
						/>

						{/* 200px rule plus the dot, straight off the export. INSIDE the held scene with the sentence:
												    after the stage it was separated from it by the bottom half of the pin, and read as a mark
												    floating in the dark rather than the line that carries the eye to the collage. Its `pt-16`
												    is taken back in `theme.css` — the export puts the rule 3px under the paragraph. */}
						<div aria-hidden className="new-story-rule hidden flex-col items-center pt-16 md:flex">
							<span className="new-story-rule-line bg-white/25" />
							<RuleDot className="new-story-rule-dot" />
						</div>
					</div>
				</div>

				{/* The collage ARRIVES, and the two breakpoints differently — see the `new-story` block in
									    `-styles/theme.css`. At `xl` the scroll walks the hand-off's five keyframes; below it the
									    three pieces stack in the flow and take the site's ordinary staggered sweep, which the
									    stylesheet stands down at `xl` because the two animate the same property.

									    The second held scene. Its stage carries the timeline: a pinned collage no longer moves
									    through the viewport, so a timeline named on the collage itself would freeze. */}
				<div className="new-story-collage-stage">
					<div className="new-story-collage-pin">
						{/* The roomier stagger, so the below-`xl` arrival is paced like the scroll-driven one
						    above rather than snapping through in a third of the time. */}
						<ExpandGroup step={EXPAND_STEP_ROOMY} asChild>
							<div className="new-story relative mt-12 flex flex-col items-center gap-8 md:mt-20 xl:mt-32 xl:block xl:h-[489px]">
								{/* 578x393, placed FROM THE CARD rather than from the band's left edge. The export's `x: -185`
														    is measured on a 1440 canvas and this box is that canvas less the container's `px-10`, so
														    applying it here left the photo overlapping the card by 8px while its opposite number sat
														    72px clear, where the export gives both 32. The card is centred less its own 16px and 558
														    wide, so its left edge is at `50% - 295px`; at 1440 this lands on -185 exactly. */}
								<Expand index={0} asChild>
									<div className="new-story-piece new-story-photo new-story-photo-l w-full max-w-[578px] overflow-hidden rounded-3xl xl:absolute xl:top-[153px] xl:left-[calc(50%_-_905px)] xl:w-[578px]">
										<Image
											src="/redesign/about/boy-playground.jpg"
											alt={t`A boy playing in a playground`}
											height={786}
											aspectRatio={578 / 393}
											objectFit="cover"
											className="h-full w-full object-cover!"
										/>
									</div>
								</Expand>

								{/* 558x364, offset 16px left of centre, and the only apricot surface on this dark band. `z-10` is
														    load-bearing, not stacking hygiene: the whole first half of the sequence is the photos
														    being hidden behind it. */}
								<Expand index={1} asChild>
									<div className="new-story-piece new-story-card bg-apricot text-ink relative z-10 flex w-full max-w-[558px] flex-col gap-6 rounded-3xl p-8 xl:absolute xl:top-0 xl:left-1/2 xl:-ml-4 xl:min-h-[364px] xl:w-[558px] xl:-translate-x-1/2">
										<Zap className="text-brand size-8 shrink-0 fill-current" strokeWidth={0} />
										<Karaoke
											className="max-w-[494px] text-xl leading-[1.3] tracking-[-0.02em] md:text-2xl xl:text-[2rem]"
											text={t`Since then, we have been building innovative fundraising tools so that companies, their employees and their customers can add a small microdonation to their everyday transactions.`}
										/>
									</div>
								</Expand>

								{/* 385x393, the mirror of the note above: 32px past the card's right edge. It runs off the right
														    edge just as the other runs off the left. */}
								<Expand index={2} asChild>
									<div className="new-story-piece new-story-photo new-story-photo-r w-full max-w-[385px] overflow-hidden rounded-3xl xl:absolute xl:top-[64px] xl:left-[calc(50%_+_295px)] xl:w-[385px]">
										<Image
											src="/redesign/about/girl-laughing.jpg"
											alt={t`A girl laughing outdoors`}
											height={786}
											aspectRatio={385 / 393}
											objectFit="cover"
											className="h-full w-full object-cover!"
										/>
									</div>
								</Expand>
							</div>
						</ExpandGroup>
					</div>
				</div>

				<p className="new-story-closing mx-auto mt-16 max-w-[943px] text-center text-xl leading-[1.3] font-medium md:mt-24 md:text-[2rem]">
					{t`Our premise is clear: to connect companies with `}
					<Squiggle lineClassName="text-olive">{t`social causes`}</Squiggle>
					{t` efficiently, securely and transparently.`}
				</p>
			</div>
		</section>
	);
}
